import React, { useState } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { DocumentNew } from 'domain/entities/document'
import * as S from './styles'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import { ReactComponent as DownloadIcon } from 'presentation/assets/icons/DonwloadIcon.svg'
import moment from 'moment-timezone'
import { DocumentStatusEnum } from 'common/enum/document-status'
import { useServices } from 'presentation/hooks/use-services'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'
import { getDocumentsLabelLocationByEnum } from 'presentation/utils/document-types-location'
import Loading from 'presentation/shared/components/Loading'

type Props = {
  document: DocumentNew
  multipleDocuments?: boolean
  documentIndex: number
  surgicalPendencyId?: number
  surgicalOrderId?: number
  refreshDocuments?: (surgicalPendencyId: number) => void
} & WithLoadingProps

const ValidateDocumentForm = WithLoading(
  ({
    document,
    multipleDocuments = false,
    documentIndex,
    surgicalPendencyId,
    surgicalOrderId,
    refreshDocuments,
    setIsLoading
  }: Props) => {
    const documentService = useServices().document
    const surgicalPendencyService = useServices().surgicalPendency
    const [downloadIsLoading, setDownloadIsLoading] = useState<boolean>(false)

    const formik = useFormik({
      initialValues: {
        id: document.document_id,
        status: document.status,
        surgical_pendency_id: surgicalPendencyId,
        surgical_order_id: surgicalOrderId
      } as ValidateDocumentFormValues,
      enableReinitialize: true,
      onSubmit: async (values) => {
        try {
          setIsLoading(true)
          await documentService.updateDocumentStatus(values)
          if (surgicalPendencyId && refreshDocuments) {
            refreshDocuments(surgicalPendencyId)
          }
          toast.success('O status do documento foi atualizado!')
        } catch {
          toast.error('Ocorreu um erro, tente novamente mais tarde.')
        } finally {
          setIsLoading(false)
        }
      }
    })

    const handleChangeStatus = (value: string) => {
      formik.setFieldValue('status', value)
      formik.submitForm()
    }

    const handleDownloadFile = async () => {
      try {
        setDownloadIsLoading(true)
        const surgicalPendencyDocument =
          await surgicalPendencyService.loadSurgicalPendencyDocument(
            Number(surgicalPendencyId),
            document.document_id,
            document.group_id
          )
        if (surgicalPendencyDocument) {
          downloadFileFromBlob(
            surgicalPendencyDocument?.data,
            surgicalPendencyDocument?.contentType,
            getDocumentsLabelLocationByEnum(document.type, true)
          )
        }
      } catch (error) {
        toast.error('Ocorreu um erro ao tentar baixar o documento')
      } finally {
        setDownloadIsLoading(false)
      }
    }

    return (
      <S.Wrapper role="form" onSubmit={formik.handleSubmit}>
        <S.CardHeader>
          <SupportTextNew
            className="filename"
            color="neutral800"
            weight="semiBold"
          >
            {`${document.type} ${multipleDocuments ? documentIndex + 1 : ''}`}
          </SupportTextNew>
          <S.ActionButtons>
            {downloadIsLoading ? (
              <Loading width="20px" height="20px" />
            ) : (
              <DownloadIcon
                role="button"
                className="btn"
                onClick={handleDownloadFile}
              />
            )}
          </S.ActionButtons>
        </S.CardHeader>
        <S.CardContent>
          <SelectFieldNew
            className={`select-status ${
              formik.values.status === DocumentStatusEnum.APPROVED
                ? '-status-approved'
                : formik.values.status === DocumentStatusEnum.DISAPPROVED
                ? '-status-disapproved'
                : ''
            }`}
            style={{ marginTop: '16px' }}
            name="status"
            items={[
              {
                label: 'Validado',
                value: DocumentStatusEnum.APPROVED
              },
              {
                label: 'Invalidado',
                value: DocumentStatusEnum.DISAPPROVED
              },
              {
                label: 'Pendente de validação',
                value: DocumentStatusEnum.ANALYZING
              }
            ]}
            value={formik.values.status}
            onInputChange={(value) => handleChangeStatus(value)}
          />

          <SupportTextNew
            style={{ marginTop: '16px' }}
            size="xxsmall"
            color="neutral600"
            lineHeight="small"
          >
            Inclusão
          </SupportTextNew>
          <SupportTextNew size="xsmall" color="neutral900" lineHeight="medium">
            Incluido em {moment(document.sended_at).format('DD MMM YYYY')} por{' '}
            {document.created_by?.name
              ?.split(' ')
              .map((word: string) => {
                return (
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
              })
              .join(' ')}
          </SupportTextNew>

          {document.status !== DocumentStatusEnum.ANALYZING && (
            <>
              <SupportTextNew
                size="xxsmall"
                color="neutral600"
                lineHeight="small"
                style={{ marginTop: '4px' }}
              >
                Validação
              </SupportTextNew>
              <SupportTextNew
                size="xsmall"
                color="neutral900"
                lineHeight="medium"
              >
                {document.status === DocumentStatusEnum.APPROVED
                  ? `Validado em ${moment(document.status_updated_at).format(
                      'DD MMM YYYY'
                    )} por ${document.status_updated_by?.name}`
                  : `Invalidado em ${moment(document.status_updated_at).format(
                      'DD MMM YYYY'
                    )} por ${document.status_updated_by?.name}`}
              </SupportTextNew>
            </>
          )}
        </S.CardContent>
      </S.Wrapper>
    )
  }
)

type ValidateDocumentFormValues = {
  id: number
  surgical_pendency_id: number
  surgical_order_id: number
  status: DocumentStatusEnum
}

export default ValidateDocumentForm
